import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useVentures() {
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ventures`);
      setVentures(res.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { ventures, loading, error, refetch: fetch };
}

export function useAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem('cv_admin_token'));
  const [contacts, setContacts] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const login = async (username, password) => {
    const res = await axios.post(`${API}/admin/login`, { username, password });
    localStorage.setItem('cv_admin_token', res.data.token);
    setToken(res.data.token);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('cv_admin_token');
    setToken(null);
  };

  const fetchContacts = useCallback(async (searchParams = {}) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.q) params.set('q', searchParams.q);
      if (searchParams.unread) params.set('unread', 'true');
      const qs = params.toString();
      const url = `${API}/contacts${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(res.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchVentures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ventures`);
      setVentures(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  const markContactRead = async (id) => {
    await axios.patch(`${API}/contacts/${id}/read`, {}, { headers });
    await fetchContacts();
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API}/contacts/${id}`, { headers });
    await fetchContacts();
  };

  const createVenture = async (data) => {
    await axios.post(`${API}/ventures`, data, { headers });
    await fetchVentures();
  };

  const updateVenture = async (id, data) => {
    await axios.put(`${API}/ventures/${id}`, data, { headers });
    await fetchVentures();
  };

  const deleteVenture = async (id) => {
    await axios.delete(`${API}/ventures/${id}`, { headers });
    await fetchVentures();
  };

  const reorderVenture = async (id, sortOrder) => {
    await axios.patch(`${API}/ventures/${id}/reorder`, { sort_order: sortOrder }, { headers });
    await fetchVentures();
  };

  return {
    token, login, logout, loading,
    contacts, fetchContacts, markContactRead, deleteContact,
    ventures, fetchVentures, createVenture, updateVenture, deleteVenture, reorderVenture,
  };
}
